import React, { useState } from 'react';
import styled from 'styled-components';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { updatePinForDirectKey } from 'api/SupraLockboxController';
import {
  Color,
  Icon,
  SemanticColor,
  Text,
  Input,
  message,
  Modal,
} from '@bb/elements-web';
import { useSupraAccessDetailsPageContext } from '../SupraAccessDetailsPageContext';

interface ChangePinModalProps {
  visible: boolean;
  existingPin: string;
  onClose: () => void;
}

const SUPRA_PIN_LENGTH = 4;
const INCORRECT_PIN_ERROR = 'INCORRECT_PIN';

const ChangePinModal: React.FC<ChangePinModalProps> = ({
  visible,
  existingPin,
  onClose,
}) => {
  const { userId, orgId, refetch } = useSupraAccessDetailsPageContext();

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [serverError, setServerError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [visibility, setVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const isNumericFour = (value: string) => /^\d{0,4}$/.test(value);

  const setIfValid = (setter: (v: string) => void) => (value: string) => {
    if (isNumericFour(value)) {
      setter(value);
      setServerError(undefined);
    }
  };

  const toggleVisibility = (field: keyof typeof visibility) => {
    setVisibility(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const currentPinIncorrect =
    currentPin.length === SUPRA_PIN_LENGTH && currentPin !== existingPin;

  const newPinSameAsCurrent =
    newPin.length === SUPRA_PIN_LENGTH &&
    currentPin.length === SUPRA_PIN_LENGTH &&
    newPin === currentPin;

  const confirmPinMismatch =
    confirmPin.length === SUPRA_PIN_LENGTH &&
    newPin.length === SUPRA_PIN_LENGTH &&
    confirmPin !== newPin;

  const allFieldsFilled =
    currentPin.length === SUPRA_PIN_LENGTH &&
    newPin.length === SUPRA_PIN_LENGTH &&
    confirmPin.length === SUPRA_PIN_LENGTH;

  const canSubmit =
    allFieldsFilled &&
    !newPinSameAsCurrent &&
    !confirmPinMismatch &&
    !currentPinIncorrect &&
    !submitting;

  const reset = () => {
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setServerError(undefined);
    setSubmitting(false);
    setVisibility({ current: false, new: false, confirm: false });
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError(undefined);
    try {
      const error = await updatePinForDirectKey({
        currentPin,
        newPin,
        userId,
        orgId,
      });
      if (error) {
        setServerError(error);
        setSubmitting(false);
        return;
      }
      message.success('Your PIN has been updated.');
      await refetch();
      setSubmitting(false);
      reset();
      onClose();
    } catch {
      setServerError('Unable to update PIN. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <StyledModal
      open={visible}
      title="Change your PIN code"
      onCancel={handleClose}
      okText="Confirm"
      cancelText="Cancel"
      dataCy="change-pin-modal"
      okButtonProps={{
        disabled: !canSubmit,
        loading: submitting,
      }}
      cancelButtonProps={{
        disabled: submitting,
      }}
      onOk={handleSubmit}
      width={500}
    >
      <Description>
        Your 4 digit PIN gives you secure access to Supra lockboxes.
      </Description>

      <Field>
        <FieldLabel>Current PIN</FieldLabel>
        <InputWithToggle masked={!visibility.current}>
          <Input
            dataCy="change-pin-current"
            value={currentPin}
            onChange={setIfValid(setCurrentPin)}
            disabled={submitting}
          />
          <ToggleButton
            type="button"
            onClick={() => toggleVisibility('current')}
            tabIndex={-1}
            aria-label={visibility.current ? 'Hide PIN' : 'Show PIN'}
          >
            {visibility.current ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </ToggleButton>
        </InputWithToggle>
        {(currentPinIncorrect || serverError === INCORRECT_PIN_ERROR) && (
          <ErrorText>
            <Icon icon="warningOutlined" color="danger" />
            <span> Your PIN is incorrect. Please try again.</span>
          </ErrorText>
        )}
        {serverError && serverError !== INCORRECT_PIN_ERROR && (
          <ErrorText>
            <Icon icon="warningOutlined" color="danger" />
            <span>{serverError}</span>
          </ErrorText>
        )}
      </Field>

      <Field>
        <FieldLabel>New PIN</FieldLabel>
        <InputWithToggle masked={!visibility.new}>
          <Input
            dataCy="change-pin-new"
            value={newPin}
            onChange={setIfValid(setNewPin)}
            disabled={submitting}
          />
          <ToggleButton
            type="button"
            onClick={() => toggleVisibility('new')}
            tabIndex={-1}
            aria-label={visibility.new ? 'Hide PIN' : 'Show PIN'}
          >
            {visibility.new ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </ToggleButton>
        </InputWithToggle>
        {newPinSameAsCurrent && (
          <ErrorText>
            <Icon icon="warningOutlined" color="danger" />
            <span> Cannot be the same as your current PIN</span>
          </ErrorText>
        )}
      </Field>

      <Field>
        <FieldLabel>Confirm New PIN</FieldLabel>
        <InputWithToggle masked={!visibility.confirm}>
          <Input
            dataCy="change-pin-confirm"
            value={confirmPin}
            onChange={setIfValid(setConfirmPin)}
            disabled={submitting}
          />
          <ToggleButton
            type="button"
            onClick={() => toggleVisibility('confirm')}
            tabIndex={-1}
            aria-label={visibility.confirm ? 'Hide PIN' : 'Show PIN'}
          >
            {visibility.confirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </ToggleButton>
        </InputWithToggle>
        {confirmPinMismatch && (
          <ErrorText>
            <Icon icon="warningOutlined" color="danger" />
            <span> Your PIN does not match. Please try again.</span>
          </ErrorText>
        )}
      </Field>
    </StyledModal>
  );
};

export default ChangePinModal;

const StyledModal = styled(Modal)`
  &&& .ant5-modal-title {
    padding: 26px 26px 0 26px;
    margin-bottom: 32px;

    .ant5-typography {
      margin-bottom: 0;
    }
  }

  &&& .ant5-modal-body {
    padding: 0px 26px 6px 26px;
  }

  &&& .ant5-modal-footer > div {
    padding-bottom: 42px !important;
    gap: 0px;
  }
`;

const Description = styled(Text)`
  display: block;
  color: ${Color.BODY_TEXT};
  margin-bottom: 16px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.div`
  font-size: 14px;
  color: ${Color.PRIMARY_TEXT};
  margin-bottom: 4px;
`;

const InputWithToggle = styled.div<{ masked: boolean }>`
  position: relative;
  flex: 1;

  ${({ masked }) =>
    masked &&
    `
    .elements-input input {
      -webkit-text-security: disc !important;
    }
  `}

  /* Add right padding to the input so text doesn't run under the toggle */
  .elements-input input {
    padding-right: 36px;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${Color.BODY_TEXT};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    color: ${Color.PRIMARY_TEXT};
  }
`;

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${SemanticColor.danger};
  display: inline-flex;
  align-items: center;

  .anticon {
    margin-right: 8px;
    flex-shrink: 0;
  }
`;
